import { useCallback, useEffect, useMemo, useState } from "react";
import { downloadJobsheet, fetchJobsheetContent } from "../service";
import DivSpinner from "../../../components/divSpinner";
import JobsheetImages from "./JobsheetImages";
import { theme } from "../../../theme";
import BtnSpinner from "../../../components/btnSpinner";
import Map from "./Map";

const Body = ({ job_id }) => {
    const [loading, setLoading] = useState(false);
    const [content, setContent] = useState(null);
    const [downloadBtnLoading, setDownloadBtnLoading] = useState(false);

    const getInvoicesContent = useCallback(async () => {
        if (!job_id) return;

        setLoading(true);
        try {
            const response = await fetchJobsheetContent(job_id);
            setContent(response?.data || null);
        } catch (error) {
            console.error("Error fetching jobsheet content:", error);
        } finally {
            setLoading(false);
        }
    }, [job_id]);

    useEffect(() => {
        getInvoicesContent();
    }, [getInvoicesContent]);

    const handleDownload = useCallback(async () => {
        if (!job_id) return;

        setDownloadBtnLoading(true);
        try {
            await downloadJobsheet(job_id);
        } catch (error) {
            console.error("Error downloading jobsheet:", error);
        } finally {
            setDownloadBtnLoading(false);
        }
    }, [job_id]);

    const invoice = useMemo(() => {
        if (!content) return {};
        return content;
    }, [content]);

    if (loading) {
        return (
            <div style={{ height: `${window.innerHeight - 387}px` }}>
                <DivSpinner />
            </div>
        );
    }

    if (!content) return null;

    const {
        job_createdAt,
        job_address,
        job_location,
        payment_working_hours,
        customer,
        ftm,
        skill,
        company,
        ftm_requests,
        jobsheet
    } = invoice;
    return (
        <div className="container-fluid py-3">
            <div className="row">
                <div className="col-md-10 col-lg-8 col-sm-10 mx-auto">
                    <div className="card">
                        <div className="card-header text-center">
                            <div className="row justify-content-between">
                                <div className="col-md-4 text-start">
                                    <img
                                        className="mb-2 w-50 p-2"
                                        src={`/assets/img/instafix-logo-1.png`}
                                        alt="Logo"
                                    />
                                    <h6 className="mb-0">
                                        {company?.company_address}
                                    </h6>
                                    <p className="d-block text-secondary mb-0">
                                        Email: {company?.company_email}
                                    </p>
                                    <p className="d-block text-secondary">
                                        Phone: {company?.company_contact_number}
                                    </p>
                                </div>

                                <div className="col-lg-3 col-md-7 text-md-end text-start mt-5">
                                    <h6 className="d-block mt-2 mb-0">
                                        {customer?.full_name}
                                    </h6>
                                    <p className="text-secondary mb-0">
                                        Email: {customer?.email}
                                    </p>
                                    <p className="text-secondary">
                                        Phone: {customer?.contact_number}
                                    </p>
                                </div>
                            </div>
                            <br />
                            <div className="row justify-content-md-between">
                                <div className="col-md-4 mt-auto">
                                    <h6 className="mb-0 text-start text-secondary font-weight-normal">
                                        Job id
                                    </h6>
                                    <h5 className="text-start mb-0">
                                        #{job_id}
                                    </h5>
                                </div>
                                <div className="col-lg-5 col-md-7 mt-auto">
                                    <div className="row mt-md-5 mt-4 text-md-end text-start">
                                        <div className="col-md-6">
                                            <h6 className="text-secondary font-weight-normal mb-0">Job created at:</h6>
                                        </div>
                                        <div className="col-md-6">
                                            <h6 className="text-dark mb-0">{job_createdAt}</h6>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <br />

                            <div className="row justify-content-md-between">
                                <div className="col-md-4 mt-auto">
                                    <h6 className="mb-0 text-start text-secondary font-weight-normal">
                                        Job details
                                    </h6>
                                    <h6 className="text-start mb-0">
                                        {skill?.name} - {skill?.sub_skill?.name}
                                    </h6>
                                    <h6 className="text-start mb-0">
                                        {job_address}
                                    </h6>
                                </div>

                                <div className="col-md-4 mt-auto">
                                    <h6 className="mb-0 text-end text-secondary font-weight-normal">
                                        Tradesperson details
                                    </h6>
                                    <h6 className="text-end mb-0">
                                        {ftm?.full_name}
                                    </h6>
                                    <h6 className="text-end mb-0">
                                        {ftm?.vehicle_number}
                                    </h6>
                                </div>
                            </div>
                        </div>
                        <div className="card-body">
                            <div className="row mb-4 mt-2">
                                <div className="col-lg-12 text-left">
                                    <h5>Description</h5>
                                    <p className="text-secondary">{jobsheet?.job_sheet_description}</p>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-12">
                                    <div className="table-responsive">
                                        <table className="table text-right">
                                            <tfoot>
                                                <tr>
                                                    <th className="h4 ps-2" colSpan="2">Working hours</th>
                                                    <th colSpan="1" className="text-end h4 ps-2">{payment_working_hours}</th>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            {ftm_requests?.length > 0 && (
                                <>
                                    {ftm_requests?.map((request, index) => (
                                        <div className="row mt-4" key={index}>
                                            <div className="col-12">
                                                <h6 className="text-dark mb-0">Request ID: {request.ftm_request_id}</h6>
                                                <p className="text-secondary mb-3">{request.ftm_request_description}</p>
                                                <div className="table-responsive">
                                                    <table className="table text-right">
                                                        <thead>
                                                            <tr>
                                                                <th scope="col" className="pe-2 text-start ps-2">Type</th>
                                                                <th scope="col" className="pe-2">Qty</th>
                                                                <th scope="col" className="pe-2 text-start">Note</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {request?.request_materials?.map((item, itemIndex) => (
                                                                <tr key={itemIndex}>
                                                                    <td className="ps-2 text-start">{item.request_material_item}</td>
                                                                    <td className="ps-4">{item.request_material_qty}</td>
                                                                    <td className="pe-2 text-start">{item.request_material_description}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </>
                            )}

                            {jobsheet?.job_sheet_images?.length > 0 && (
                                <>
                                    <div className="row mt-4">
                                        <div className="col-12">
                                            <h6 className="text-dark mb-0">Jobsheet Images</h6>
                                            <JobsheetImages job_sheet_images={jobsheet?.job_sheet_images} />
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="row mt-4">
                                <div className="col-12">
                                    <h6 className="text-dark mb-4">Job location</h6>
                                    <Map job_location={job_location} />
                                </div>
                            </div>
                        </div>
                        
                        <div className="card-footer">
                            <div className="row">
                                <div className="col-lg-5 text-left"></div>
                                <div className="col-lg-7 text-md-end mt-md-0 mt-3">
                                    <button
                                        className={`btn btn-sm btn-icon btn-3 btn-${theme.main}`}
                                        type="button"
                                        onClick={handleDownload}
                                        disabled={downloadBtnLoading}
                                    >
                                        {downloadBtnLoading ? (
                                            <BtnSpinner />
                                        ) : (
                                            <>
                                                <span className="btn-inner--icon">
                                                    <i className="material-icons text-lg">
                                                        download
                                                    </i>
                                                </span>
                                                <span className="btn-inner--text">
                                                    Download jobsheet
                                                </span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Body;